
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import { debounce } from 'lodash';

import { sharedState } from '../../../../../../framework/state';
import { getResource, putResource } from '../../../../../../framework/client/resource';
import { getDatabaseOptions } from '../../../../../../framework/service/options';

import TextField from '../../../../../../framework/fields/TextField.vue';
import SelectField from '../../../../../../framework/fields/SelectField.vue';
import SwitchField from '../../../../../../framework/fields/SwitchField.vue';
import IntegerField from '../../../../../../framework/fields/IntegerField.vue';
import CurrencyAmountField from '../../../../../../framework/fields/CurrencyAmountField.vue';
import NumberField from '../../../../../../framework/fields/NumberField.vue';
import PercentageField from '../../../../../../framework/fields/PercentageField.vue';
import DateField from '../../../../../../framework/fields/DateField.vue';
import AutoCompleteField from '../../../../../../framework/fields/AutoCompleteField.vue';
import VendorContractSelect from '../../../../../fields/VendorContractSelect.vue';

import { SalesInvoiceRow } from '../../../../../../../common/application/model/SalesInvoiceRow';
import { ApplicationResource } from '../../../../../../../common/application/enumeration/ApplicationResource';
import { successToast, errorToast } from '../../../../../service/toast_service';
import { updateCachedResourceRow } from '../../../../../service/query_cache_service';
import { calculateInvoiceRowSums } from '../../../../../service/helper_service';

@Component({
    components: {
        DateField,
        PercentageField,
        NumberField,
        CurrencyAmountField,
        SelectField,
        TextField,
        SwitchField,
        IntegerField,
        AutoCompleteField,
        VendorContractSelect,
        ValidationObserver,
    },
})
export default class EditSalesInvoiceRow extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.SALES_INVOICE_ROW;

    // Properties
    @Prop(String) readonly id!: string;

    // State
    shared = sharedState;
    local = {
        row: {} as SalesInvoiceRow,
        invoiceRowTypeOptions: [] as { id: string | undefined; label: string }[],
    };

    // Functions
    async mounted() {
        this.local.row = (await getResource<SalesInvoiceRow>(this.resourceType, this.id))!!;
        this.local.invoiceRowTypeOptions = await getDatabaseOptions('InvoiceRowType', this);
    }

    @Watch('local.row.quantity')
    async quantityChanged(quantity: number) {
        if (!quantity) {
            return;
        }

        this.debounceSumCalculation();
    }

    @Watch('local.row.vatPercentage')
    async vatPercentageChanged(vatPercentage: number) {
        if (!vatPercentage) {
            return;
        }

        this.debounceSumCalculation();
    }

    @Watch('local.row.unitPrice')
    async unitPriceChanged(unitPrice: number) {
        if (!unitPrice) {
            return;
        }

        this.debounceSumCalculation();
    }

    save() {
        this.saveResource(false);
    }

    saveAndClose() {
        this.saveResource(true);
    }

    back() {
        this.$router.go(-1);
    }

    private async saveResource(goBack: boolean) {
        try {
            const updatedRow = await putResource<SalesInvoiceRow>(this.resourceType, this.id, this.local.row);

            successToast(this, 'message.salesInvoiceRowSaved');
            updateCachedResourceRow<SalesInvoiceRow>(updatedRow, this.id, this.resourceType);

            if (goBack) {
                this.back();
            }
        } catch (error) {
            errorToast(this);
        }
    }

    private debounceSumCalculation = debounce(() => {
        this.calculateRowSums();
    }, 500);

    private calculateRowSums() {
        const { sumVat, sumVatExcluded, sumVatIncluded } = calculateInvoiceRowSums(this.local.row);
        this.local.row = {
            ...this.local.row,
            sumVat,
            sumVatExcluded,
            sumVatIncluded,
        };
    }
}
